/**
 * ClientDesk ↔ AuditFlow data bridge.
 *
 * The two modules share one data pool. They do not copy data between each
 * other — they both read and write to the same canonical records. The bridge
 * defines what those shared records are, and the contracts that govern which
 * module "owns" each type.
 *
 * Ownership rules:
 *
 *   ClientDesk owns:  the client master, contacts, DSC, credentials,
 *                     compliance calendar, billing, relationship health.
 *                     AuditFlow reads these but does not write them.
 *
 *   AuditFlow owns:   engagements, workpapers, risk assessments, findings,
 *                     trial balance data, audit programs, sign-offs.
 *                     ClientDesk reads engagement status and findings but
 *                     does not write them.
 *
 *   Shared:           documents (either module can add; both can read),
 *                     comments/queries (both modules, linked by context).
 *
 * This is the architecture that makes the product genuinely different from
 * Inflo + a separate CRM: a finding raised in AuditFlow automatically shows
 * on the ClientDesk compliance health card. An engagement opened in AuditFlow
 * automatically creates the corresponding ClientDesk task and compliance
 * calendar entries. No manual sync, no duplicate data entry.
 *
 * An engagement always originates in AuditFlow (as you stated). ClientDesk
 * reflects its existence and progress but does not create engagements itself.
 */

import type { Client, ComplianceItem, Engagement, ReturnType } from "../clientdesk/types";

// ----------------------------------------------- Shared record types

export type SharedDocumentContext =
  | { module: "clientdesk"; recordType: "client" | "compliance-item" | "query" }
  | { module: "auditflow"; recordType: "engagement" | "workpaper" | "finding" | "pbc-request" };

export interface SharedDocument {
  id: string;
  name: string;
  category: string;
  /** Which module uploaded this and which record it belongs to. */
  context: SharedDocumentContext;
  contextId: string;
  /** Visible to the audit client in the client portal. */
  clientVisible: boolean;
  fileUrl?: string;
  fileSize?: number;
  mimeType?: string;
  uploadedBy: string;
  uploadedAt: string;
  /** Version sequence within this document name. */
  version: number;
  tags?: string[];
}

export interface SharedComment {
  id: string;
  text: string;
  author: string;
  at: string;
  module: "clientdesk" | "auditflow";
  contextId: string;
  contextType: string;
  mentions?: string[];
  /** When set, this comment is a reply. */
  replyToId?: string;
}

// ----------------------------------------------- Events that cross the bridge

/**
 * An AuditEngagementCreated event is published by AuditFlow and consumed by
 * ClientDesk to: create the corresponding engagement record on the client
 * master, add the audit return items to the compliance calendar, and create
 * tasks in the practice task list.
 *
 * This is the starting point you specified — engagement creation always
 * happens in AuditFlow, and the downstream effects in ClientDesk are
 * automatic.
 */
export interface AuditEngagementCreated {
  type: "AuditEngagementCreated";
  engagementId: string;
  clientId: string;
  engagementType: Engagement["type"];
  financialYear: string;
  period: string;
  partner: string;
  manager?: string;
  team: string[];
  /** Which compliance return types this engagement covers. */
  coversReturns: ReturnType[];
  createdAt: string;
  createdBy: string;
}

/**
 * AuditFlow publishes this when the engagement moves to a new stage.
 * ClientDesk updates the engagement record on the client master and, where
 * relevant, the compliance calendar item (e.g. "completion" → marks the
 * audit return as filed).
 */
export interface AuditEngagementStageChanged {
  type: "AuditEngagementStageChanged";
  engagementId: string;
  clientId: string;
  fromStage: Engagement["status"];
  toStage: Engagement["status"];
  changedBy: string;
  changedAt: string;
  note?: string;
}

/**
 * When the audit is signed off, AuditFlow publishes this event. ClientDesk
 * uses it to:
 *   - Mark the audit return as "filed" on the compliance calendar
 *   - Update the compliance score
 *   - Generate a billing record for the engagement fee
 */
export interface AuditSignedOff {
  type: "AuditSignedOff";
  engagementId: string;
  clientId: string;
  signedOffBy: string;
  signedOffAt: string;
  reportDate: string;
  /** Audit opinion — used in the compliance record. */
  opinion: AuditOpinion;
  returnsCompleted: ReturnType[];
  acknowledgmentNumbers?: Record<ReturnType, string>;
}

/**
 * AuditFlow publishes findings so they appear on the ClientDesk health card
 * without the partner having to check both modules. A critical finding bumps
 * the relationship health score down immediately.
 */
export interface AuditFindingRaised {
  type: "AuditFindingRaised";
  findingId: string;
  engagementId: string;
  clientId: string;
  severity: FindingSeverity;
  area: string;
  description: string;
  raisedBy: string;
  raisedAt: string;
}

export interface AuditFindingResolved {
  type: "AuditFindingResolved";
  findingId: string;
  engagementId: string;
  clientId: string;
  resolvedBy: string;
  resolvedAt: string;
  resolution: string;
}

/**
 * ClientDesk publishes this when client financial data is updated (from
 * LedzerlyBooks or manual entry). AuditFlow picks it up to pre-populate the
 * trial balance and financial snapshot on the engagement.
 */
export interface ClientFinancialsUpdated {
  type: "ClientFinancialsUpdated";
  clientId: string;
  financialYear: string;
  source: "ledzerlybooks" | "manual";
  turnover?: number;
  netProfit?: number;
  taxableIncome?: number;
  updatedAt: string;
}

export type BridgeEvent =
  | AuditEngagementCreated
  | AuditEngagementStageChanged
  | AuditSignedOff
  | AuditFindingRaised
  | AuditFindingResolved
  | ClientFinancialsUpdated;

// -------------------------------- What ClientDesk reads from AuditFlow

/**
 * The engagement summary that ClientDesk shows on the client detail card.
 * AuditFlow writes this; ClientDesk reads it. This is the projection that
 * flows across the bridge, not the full engagement record.
 */
export interface EngagementSummary {
  engagementId: string;
  clientId: string;
  type: Engagement["type"];
  financialYear: string;
  status: Engagement["status"];
  partner: string;
  reportDate?: string;
  opinion?: AuditOpinion;
  openFindings: number;
  criticalFindings: number;
  completionPercent: number;
  /** What ClientDesk shows under "Last audit activity". */
  lastActivity: string;
  lastActivityAt: string;
}

// ------------------------------ What AuditFlow reads from ClientDesk

/**
 * The client context that AuditFlow needs at engagement level. This is what
 * populates the engagement's client understanding section automatically
 * rather than the auditor re-entering data that ClientDesk already holds.
 */
export interface ClientContext {
  clientId: string;
  name: string;
  type: Client["type"];
  pan?: string;
  gstin?: string;
  cin?: string;
  gstStatus?: string;
  dateOfIncorporation?: string;
  taxRegime?: Client["taxRegime"];
  registeredAddress?: string;
  contacts: Array<{ name: string; designation?: string; email?: string; phone?: string }>;
  /** Financial data from the most recent snapshot, for preliminary analytics. */
  priorYearTurnover?: number;
  priorYearNetProfit?: number;
  /** Whether the client's books are on LedzerlyBooks (enables TB auto-import). */
  booksOnPlatform: boolean;
  linkedBooksEntityId?: string;
  complianceGrade?: string;
  lastAuditYear?: string;
  dscDetails?: Array<{ holderName: string; validTo: string; status: string }>;
}

// ----------------------------------------- Shared domain types

export type AuditOpinion =
  | "unmodified"
  | "qualified"
  | "adverse"
  | "disclaimer"
  | "emphasis-of-matter";

export type FindingSeverity = "critical" | "significant" | "moderate" | "minor" | "informational";

export const OPINION_LABELS: Record<AuditOpinion, string> = {
  unmodified: "Unmodified (clean)",
  qualified: "Qualified",
  adverse: "Adverse",
  disclaimer: "Disclaimer of opinion",
  "emphasis-of-matter": "Unmodified with emphasis of matter"
};

export const FINDING_SEVERITY_LABELS: Record<FindingSeverity, string> = {
  critical: "Critical",
  significant: "Significant",
  moderate: "Moderate",
  minor: "Minor",
  informational: "Informational"
};

// ----------------------------------------- Bridge processor (in-process)

/**
 * Processes a bridge event and returns what each module should update.
 *
 * In production this will be a message queue or a server-side event handler.
 * For the current in-process architecture it runs synchronously so the UI
 * stays consistent — the same logic applies either way.
 */
export const processBridgeEvent = (
  event: BridgeEvent,
  context: { clients: Client[]; complianceItems: ComplianceItem[] }
): {
  clientdeskUpdates: Array<{ type: string; payload: unknown }>;
  auditflowUpdates: Array<{ type: string; payload: unknown }>;
} => {
  const clientdeskUpdates: Array<{ type: string; payload: unknown }> = [];
  const auditflowUpdates: Array<{ type: string; payload: unknown }> = [];

  switch (event.type) {
    case "AuditEngagementCreated": {
      // ClientDesk: add engagement to client master, create compliance items.
      clientdeskUpdates.push({
        type: "ADD_ENGAGEMENT_TO_CLIENT",
        payload: {
          clientId: event.clientId,
          engagement: {
            id: event.engagementId,
            type: event.engagementType,
            financialYear: event.financialYear,
            period: event.period,
            status: "planning",
            partner: event.partner,
            manager: event.manager,
            team: event.team,
            auditFlowEngagementId: event.engagementId
          }
        }
      });
      // Create compliance calendar items for each return covered.
      for (const returnType of event.coversReturns) {
        clientdeskUpdates.push({
          type: "ADD_COMPLIANCE_ITEM",
          payload: {
            clientId: event.clientId,
            item: {
              returnType,
              period: event.financialYear,
              periodLabel: `AY / FY ${event.financialYear}`,
              status: "in-progress",
              taskId: undefined,
              notes: `Covered by engagement ${event.engagementId}`
            }
          }
        });
      }
      break;
    }
    case "AuditSignedOff": {
      // ClientDesk: mark covered returns as filed, update compliance score.
      for (const returnType of event.returnsCompleted) {
        clientdeskUpdates.push({
          type: "UPDATE_COMPLIANCE_ITEM",
          payload: {
            clientId: event.clientId,
            returnType,
            status: "filed",
            filedDate: event.reportDate,
            acknowledgmentNumber: event.acknowledgmentNumbers?.[returnType]
          }
        });
      }
      clientdeskUpdates.push({ type: "REFRESH_COMPLIANCE_SCORE", payload: { clientId: event.clientId } });
      break;
    }
    case "AuditFindingRaised": {
      // ClientDesk: a critical finding immediately affects relationship health.
      if (event.severity === "critical" || event.severity === "significant") {
        clientdeskUpdates.push({
          type: "FLAG_CLIENT_RISK",
          payload: { clientId: event.clientId, reason: `${event.severity} audit finding: ${event.description}` }
        });
      }
      break;
    }
    case "ClientFinancialsUpdated": {
      // AuditFlow: pre-populate the trial balance on any open engagement for this client/year.
      auditflowUpdates.push({
        type: "PRE_POPULATE_TRIAL_BALANCE",
        payload: {
          clientId: event.clientId,
          financialYear: event.financialYear,
          data: { turnover: event.turnover, netProfit: event.netProfit, source: event.source }
        }
      });
      break;
    }
    default:
      break;
  }

  return { clientdeskUpdates, auditflowUpdates };
};

/**
 * Derives the ClientContext that AuditFlow needs from a ClientDesk client
 * record. This is called when an engagement is opened in AuditFlow to
 * pre-populate the client understanding section.
 */
export const clientContextFor = (client: Client): ClientContext => ({
  clientId: client.id,
  name: client.name,
  type: client.type,
  pan: client.pan,
  gstin: client.gstin,
  cin: client.cin,
  gstStatus: client.gstStatus,
  dateOfIncorporation: client.dateOfIncorporation,
  taxRegime: client.taxRegime,
  registeredAddress: client.billingAddress
    ? [client.billingAddress.line1, client.billingAddress.city, client.billingAddress.state, client.billingAddress.pincode]
        .filter(Boolean)
        .join(", ")
    : undefined,
  contacts: (client.contacts ?? []).map((contact) => ({
    name: contact.name,
    designation: contact.designation,
    email: contact.email,
    phone: contact.phone
  })),
  booksOnPlatform: Boolean(client.linkedBooksEntityId),
  linkedBooksEntityId: client.linkedBooksEntityId,
  dscDetails: (client.dsc ?? []).map((dsc) => ({ holderName: dsc.holderName, validTo: dsc.validTo, status: dsc.status }))
});
